/*
 * PAWFEEDER cart tools — Order Note, Shipping Estimate, and Discount Code.
 *
 * These three utilities live in both the cart drawer and the /cart page. They
 * are driven entirely through event delegation on `document` using a shared set
 * of `data-pf-*` attributes, so the same runtime powers server-rendered Liquid
 * (cart page) and the drawer's re-rendered HTML without duplicated logic.
 *
 * Every lookup is scoped to the nearest `[data-pf-cart-tools]` container: on the
 * cart page the drawer's tools and the page's tools exist in the DOM at the same
 * time, so global queries would act on the wrong copy.
 *
 *   [data-pf-cart-tools]                      one tool set (row + panels)
 *   [data-pf-tool="note|shipping|discount"]   toggle button (aria-expanded)
 *   [data-pf-tool-panel="…"]                  collapsible panel (hidden)
 *   [data-pf-tool-close]                      dismisses the panel it sits in
 *   [data-pf-note-input] / [data-pf-note-save] / [data-pf-note-label]
 *   [data-pf-ship-country] / [data-pf-ship-province] / [data-pf-ship-zip]
 *     / [data-pf-ship-estimate] / [data-pf-ship-result]
 *   [data-pf-discount-input] / [data-pf-discount-apply] / [data-pf-discount-error]
 *
 * Shopify's AJAX cart API has no endpoint to *apply* a discount code to the cart
 * (codes are resolved at checkout), so a valid code is persisted and carried to
 * checkout through the standard `?discount=CODE` query parameter on
 * `[data-pf-checkout]` buttons. Final validation therefore happens at checkout.
 */
(function () {
  'use strict';

  var I18N = window.PF_I18N || {};

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------ country / province --- */

  var COUNTRIES = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'AT', name: 'Austria' },
    { code: 'SE', name: 'Sweden' },
    { code: 'PL', name: 'Poland' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'SG', name: 'Singapore' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'MX', name: 'Mexico' },
    { code: 'BR', name: 'Brazil' }
  ];

  // Subdivision lists for the countries the estimator offers, keyed by
  // ISO 3166-1 alpha-2 country code. Each entry is `CODE|Name`, where CODE is
  // the value Shopify itself uses for that subdivision - it is what we send as
  // `shipping_address[province]`, so it has to agree with the shipping zones.
  //
  // SOURCE OF TRUTH: Shopify's own country data, served to every storefront at
  //   GET /services/countries/<any country code>
  // It returns `var Countries = {...}`; each country carries `provinces` (names)
  // and `province_codes` ({ name: code }). Re-read that endpoint to refresh or
  // extend this table. Do NOT hand-write codes from ISO 3166-2 - they disagree
  // for several countries (Mexico is AGS/BC/DF/Q ROO, not AGU/BCN/CMX/ROO).
  //
  // Countries Shopify has no subdivision data for are omitted on purpose: SA and
  // TZ come back with an empty province list, so their province select stays
  // disabled and reads "Not applicable". Shopify accepts either the code or the
  // full name in `shipping_address[province]` (both verified against
  // /cart/shipping_rates.json); we send the code.
  //
  // Country order mirrors COUNTRIES above so the two lists stay easy to diff.
  var PROVINCES = {
    US: 'AL|Alabama,AK|Alaska,AS|American Samoa,AZ|Arizona,AR|Arkansas,CA|California,CO|Colorado,CT|Connecticut,DE|Delaware,FM|Federated States of Micronesia,FL|Florida,GA|Georgia,GU|Guam,HI|Hawaii,ID|Idaho,IL|Illinois,IN|Indiana,IA|Iowa,KS|Kansas,KY|Kentucky,LA|Louisiana,ME|Maine,MH|Marshall Islands,MD|Maryland,MA|Massachusetts,MI|Michigan,MN|Minnesota,MS|Mississippi,MO|Missouri,MT|Montana,NE|Nebraska,NV|Nevada,NH|New Hampshire,NJ|New Jersey,NM|New Mexico,NY|New York,NC|North Carolina,ND|North Dakota,MP|Northern Mariana Islands,OH|Ohio,OK|Oklahoma,OR|Oregon,PW|Palau,PA|Pennsylvania,PR|Puerto Rico,RI|Rhode Island,SC|South Carolina,SD|South Dakota,TN|Tennessee,TX|Texas,UT|Utah,VT|Vermont,VA|Virginia,WA|Washington,DC|District of Columbia,WV|West Virginia,WI|Wisconsin,WY|Wyoming,VI|Virgin Islands,AA|Armed Forces Americas,AE|Armed Forces Europe,AP|Armed Forces Pacific',
    CA: 'AB|Alberta,BC|British Columbia,MB|Manitoba,NB|New Brunswick,NL|Newfoundland and Labrador,NT|Northwest Territories,NS|Nova Scotia,NU|Nunavut,ON|Ontario,PE|Prince Edward Island,QC|Quebec,SK|Saskatchewan,YT|Yukon',
    GB: 'BFP|British Forces,ENG|England,NIR|Northern Ireland,SCT|Scotland,WLS|Wales',
    AU: 'ACT|Australian Capital Territory,NSW|New South Wales,NT|Northern Territory,QLD|Queensland,SA|South Australia,TAS|Tasmania,VIC|Victoria,WA|Western Australia',
    AE: 'AZ|Abu Dhabi,AJ|Ajman,DU|Dubai,FU|Fujairah,RK|Ras al-Khaimah,SH|Sharjah,UQ|Umm al-Quwain',
    MX: 'AGS|Aguascalientes,BC|Baja California,BCS|Baja California Sur,CAMP|Campeche,CHIS|Chiapas,CHIH|Chihuahua,DF|Ciudad de México,COAH|Coahuila,COL|Colima,DGO|Durango,GTO|Guanajuato,GRO|Guerrero,HGO|Hidalgo,JAL|Jalisco,MEX|México,MICH|Michoacán,MOR|Morelos,NAY|Nayarit,NL|Nuevo León,OAX|Oaxaca,PUE|Puebla,QRO|Querétaro,Q ROO|Quintana Roo,SLP|San Luis Potosí,SIN|Sinaloa,SON|Sonora,TAB|Tabasco,TAMPS|Tamaulipas,TLAX|Tlaxcala,VER|Veracruz,YUC|Yucatán,ZAC|Zacatecas',
    BR: 'AC|Acre,AL|Alagoas,AP|Amapá,AM|Amazonas,BA|Bahia,CE|Ceará,DF|Distrito Federal,ES|Espírito Santo,GO|Goiás,MA|Maranhão,MT|Mato Grosso,MS|Mato Grosso do Sul,MG|Minas Gerais,PA|Pará,PB|Paraíba,PR|Paraná,PE|Pernambuco,PI|Piauí,RJ|Rio de Janeiro,RN|Rio Grande do Norte,RS|Rio Grande do Sul,RO|Rondônia,RR|Roraima,SC|Santa Catarina,SP|São Paulo,SE|Sergipe,TO|Tocantins'
  };

  function provincesFor(code) {
    var raw = PROVINCES[code];
    if (!raw) return [];
    return raw.split(',').map(function (entry) {
      var pair = entry.split('|');
      return { code: pair[0], name: pair[1] };
    });
  }

  /* ------------------------------------------------------------ helpers --- */

  function money(cents, currency) {
    if (window.PawfeederUI && window.PawfeederUI.money) {
      return window.PawfeederUI.money(cents, currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active));
    }
    return (Number(cents || 0) / 100).toFixed(2) + (currency ? ' ' + currency : '');
  }

  function escapeHtml(value) {
    var node = document.createElement('span');
    node.textContent = value == null ? '' : String(value);
    return node.innerHTML;
  }

  function report(message) {
    if (window.PawfeederUI && window.PawfeederUI.showToast) window.PawfeederUI.showToast(message);
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error((I18N.request_failed || 'Request failed.'));
      return response.json();
    });
  }

  /* ------------------------------------------------------------ state --- */

  var note = '';
  var discount = '';
  var discountApplied = false;

  /* ------------------------------------------------------- panel toggle --- */

  // Accordion within one tool set: opening a panel collapses its siblings.
  function togglePanel(button) {
    if (!button) return;
    var container = button.closest('[data-pf-cart-tools]');
    if (!container) return;
    var target = button.getAttribute('data-pf-tool');
    var panel = container.querySelector('[data-pf-tool-panel="' + target + '"]');
    var shouldOpen = button.getAttribute('aria-expanded') !== 'true';

    if (shouldOpen) {
      container.querySelectorAll('[data-pf-tool]').forEach(function (other) {
        if (other === button) return;
        other.setAttribute('aria-expanded', 'false');
        var otherPanel = container.querySelector('[data-pf-tool-panel="' + other.getAttribute('data-pf-tool') + '"]');
        if (otherPanel) otherPanel.hidden = true;
      });
    }
    button.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    if (panel) panel.hidden = !shouldOpen;
  }

  // Dismisses the panel that owns the button and hands focus back to the icon
  // button that opened it, so keyboard users are not dropped at the top of the
  // document when the panel disappears.
  function closePanel(button) {
    if (!button) return;
    var container = button.closest('[data-pf-cart-tools]');
    var panel = button.closest('[data-pf-tool-panel]');
    if (!container || !panel) return;
    panel.hidden = true;
    var opener = container.querySelector('[data-pf-tool="' + panel.getAttribute('data-pf-tool-panel') + '"]');
    if (!opener) return;
    opener.setAttribute('aria-expanded', 'false');
    opener.focus();
  }

  /* -------------------------------------------------------- order note --- */

  function noteLabel() {
    return note.trim() ? (I18N.note_edit || 'Edit Order Note') : (I18N.note_add || 'Add Order Note');
  }

  function syncNoteLabel() {
    document.querySelectorAll('[data-pf-note-label]').forEach(function (label) {
      label.textContent = noteLabel();
    });
  }

  function saveNote(container) {
    var input = container ? container.querySelector('[data-pf-note-input]') : null;
    var text = input ? input.value : note;
    return fetchJSON(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: text })
    }).then(function () {
      note = text;
      syncNoteLabel();
      report((I18N.note_saved || 'Order note saved.'));
    }).catch(function () {
      report((I18N.note_error || 'The order note could not be saved.'));
    });
  }

  /* --------------------------------------------------- shipping estimate --- */

  function populateCountries() {
    document.querySelectorAll('[data-pf-ship-country]').forEach(function (select) {
      if (select.options.length > 1) return;
      var html = '<option value="">' + (I18N.select_country || 'Select a country') + '</option>';
      COUNTRIES.forEach(function (country) {
        html += '<option value="' + country.code + '">' + escapeHtml(country.name) + '</option>';
      });
      select.innerHTML = html;
    });
  }

  function countryName(code) {
    for (var i = 0; i < COUNTRIES.length; i++) {
      if (COUNTRIES[i].code === code) return COUNTRIES[i].name;
    }
    return code || '';
  }

  function provinceName(code, provinces) {
    for (var i = 0; i < provinces.length; i++) {
      if (provinces[i].code === code) return provinces[i].name;
    }
    return code || '';
  }

  // The province row is always visible so the panel never changes height. It is
  // only editable for the countries whose province *codes* Shopify matches
  // against shipping zones; everywhere else it stays disabled with a
  // placeholder, and estimateShipping() simply omits the parameter.
  function refreshProvince(container) {
    var country = container.querySelector('[data-pf-ship-country]');
    var province = container.querySelector('[data-pf-ship-province]');
    if (!country || !province) return;
    var provinces = provincesFor(country.value);
    if (provinces.length) {
      var html = '<option value="">' + (I18N.select_province || 'Select a province') + '</option>';
      provinces.forEach(function (entry) {
        html += '<option value="' + entry.code + '">' + escapeHtml(entry.name) + '</option>';
      });
      province.innerHTML = html;
      province.disabled = false;
      province.required = true;
    } else {
      province.innerHTML = '<option value="">'
        + (country.value ? (I18N.not_applicable || 'Not applicable') : (I18N.select_country_first || 'Select a country first')) + '</option>';
      province.disabled = true;
      province.required = false;
    }
  }

  function estimateShipping(container) {
    var country = container.querySelector('[data-pf-ship-country]');
    var province = container.querySelector('[data-pf-ship-province]');
    var zip = container.querySelector('[data-pf-ship-zip]');
    var result = container.querySelector('[data-pf-ship-result]');
    if (!country || !zip || !result) return;

    if (!country.value) {
      result.innerHTML = '<p class="pf-cart-tool-error">' + (I18N.shipping_no_country || 'Select a country first.') + '</p>';
      return;
    }

    var params = 'shipping_address[country]=' + encodeURIComponent(country.value);
    if (province && province.value) params += '&shipping_address[province]=' + encodeURIComponent(province.value);
    if (zip.value.trim()) params += '&shipping_address[zip]=' + encodeURIComponent(zip.value.trim());

    result.innerHTML = '<p class="pf-form-note">' + (I18N.shipping_estimating || 'Estimating shipping…') + '</p>';
    fetchJSON(root + 'cart/shipping_rates.json?' + params)
      .then(function (data) {
        var rates = (data && data.shipping_rates) || [];
        var provinces = provincesFor(country.value);
        var location = province && province.value
          ? provinceName(province.value, provinces) + ', ' + countryName(country.value)
          : (zip.value.trim() ? countryName(country.value) + ' ' + zip.value.trim() : countryName(country.value));

        if (!rates.length) {
          result.innerHTML = '<p class="pf-cart-tool-error">' + (I18N.shipping_none || 'No shipping rates available for this address.') + '</p>';
          return;
        }
        var head = (rates.length === 1
    ? (I18N.shipping_rate_one || 'There is 1 shipping rate available for')
    : (I18N.shipping_rate_many || 'There are @@ shipping rates available for').replace('@@', rates.length))
    + ' ' + escapeHtml(location) + ':';
        var lines = rates.map(function (rate) {
          return escapeHtml(rate.name || I18N.shipping || 'Shipping') + ' — ' + money(rate.price, rate.currency);
        }).join('<br>');
        result.innerHTML = '<p>' + head + '</p><p class="pf-cart-tool-panel__result">' + lines + '</p>';
      })
      .catch(function () {
        result.innerHTML = '<p class="pf-cart-tool-error">' + (I18N.shipping_unavailable || 'Shipping estimate is unavailable right now.') + '</p>';
      });
  }

  /* ------------------------------------------------------ discount code --- */

  function applyDiscount(container) {
    var input = container.querySelector('[data-pf-discount-input]');
    var error = container.querySelector('[data-pf-discount-error]');
    var success = container.querySelector('[data-pf-discount-success]');
    if (!input || !error) return;

    var code = (input.value || '').trim();
    if (!code) {
      error.hidden = false;
      if (success) success.hidden = true;
      input.setAttribute('aria-invalid', 'true');
      return;
    }
    error.hidden = true;
    input.removeAttribute('aria-invalid');
    discount = code;
    discountApplied = true;
    try { window.sessionStorage.setItem('pf_discount', code); } catch (ignored) { /* private mode */ }
    if (success) success.hidden = false;
    report((I18N.discount_applied || 'Discount code applied.'));
  }

  /* ------------------------------------------------------- checkout hook --- */

  // A valid code is carried into checkout via ?discount=. This is the only
  // supported way to pre-apply a code before Shopify resolves it at checkout.
  function redirectWithDiscount(event) {
    if (!discountApplied || !discount) return;
    var checkout = event.target.closest('[data-pf-checkout]');
    if (!checkout) return;
    event.preventDefault();
    window.location.assign(root + 'checkout?discount=' + encodeURIComponent(discount));
  }

  /* ----------------------------------------------------- public surface --- */

  window.PawfeederCartTools = {
    getDiscount: function () { return discountApplied ? discount : ''; },
    getNote: function () { return note; },
    // Re-read persisted note / discount from the DOM (e.g. after the drawer
    // rebuilds its footer from a fresh cart response).
    sync: function () {
      var input = document.querySelector('[data-pf-note-input]');
      if (input) note = input.value;
      syncNoteLabel();
      var discountInput = document.querySelector('[data-pf-discount-input]');
      if (discountInput && discountApplied) discountInput.value = discount;
    }
  };

  /* ----------------------------------------------------- event delegation --- */

  document.addEventListener('change', function (event) {
    var country = event.target.closest('[data-pf-ship-country]');
    if (country) {
      var panel = country.closest('[data-pf-tool-panel]');
      if (panel) refreshProvince(panel);
    }
  });

  document.addEventListener('click', function (event) {
    redirectWithDiscount(event);

    var closeButton = event.target.closest('[data-pf-tool-close]');
    if (closeButton) {
      closePanel(closeButton);
      return;
    }

    var tool = event.target.closest('[data-pf-tool]');
    if (tool) {
      togglePanel(tool);
      return;
    }

    var container = event.target.closest('[data-pf-cart-tools]');
    if (!container) return;

    if (event.target.closest('[data-pf-note-save]')) {
      saveNote(container);
      return;
    }

    if (event.target.closest('[data-pf-ship-estimate]')) {
      estimateShipping(container);
      return;
    }

    if (event.target.closest('[data-pf-discount-apply]')) {
      applyDiscount(container);
    }
  });

  /* -------------------------------------------------------------- boot --- */

  populateCountries();
  // Seed every shipping panel's province row so it shows a disabled placeholder
  // instead of an empty select before a country has been chosen.
  document.querySelectorAll('[data-pf-ship-province]').forEach(function (province) {
    var panel = province.closest('[data-pf-tool-panel]');
    if (panel) refreshProvince(panel);
  });
  var existingNote = document.querySelector('[data-pf-note-input]');
  if (existingNote) note = existingNote.value;
  syncNoteLabel();
  try {
    var storedDiscount = window.sessionStorage.getItem('pf_discount');
    if (storedDiscount) {
      discount = storedDiscount;
      discountApplied = true;
      var discountInput = document.querySelector('[data-pf-discount-input]');
      if (discountInput) discountInput.value = discount;
    }
  } catch (ignored) { /* private mode */ }
})();
